<script setup lang="ts">
import { computed } from 'vue';
import type { DirectoryDevice } from '../lib/directory.js';
import AppIcon from './AppIcon.vue';
import type { IconName } from './icons.js';

/**
 * A greyed, non-interactive preview of a tool that no host implements yet. It shows
 * the intended layout so the page has a shape, and says plainly that nothing here
 * is connected to the device.
 */
const props = defineProps<{ tool: 'files' | 'logs' | 'resources'; device: DirectoryDevice }>();

interface Blueprint {
  readonly title: string;
  readonly icon: IconName;
  readonly summary: string;
  readonly requirement: string;
  readonly bullets: readonly string[];
}
const blueprints: Readonly<Record<typeof props.tool, Blueprint>> = {
  files: {
    title: 'Files',
    icon: 'files',
    summary:
      'Browse folders on the device, upload and download files, and drop files onto a desktop session.',
    requirement: 'Needs the file capability on the owner host and an explicit file permission.',
    bullets: [
      'Browse and search folders with breadcrumbs',
      'Upload and download with progress and resume',
      'Drag files into an open desktop session',
    ],
  },
  logs: {
    title: 'Logs',
    icon: 'logs',
    summary:
      'Follow connection, session and agent events from the device, filter by level and export a range.',
    requirement: 'Needs the log stream from the device agent.',
    bullets: [
      'Live tail with pause and search',
      'Filter by level and source',
      'Export a time range as text',
    ],
  },
  resources: {
    title: 'Resources',
    icon: 'resources',
    summary: 'CPU, memory, disk and network at a glance, with the busiest processes.',
    requirement: 'Needs the diagnostic telemetry channel.',
    bullets: [
      'CPU, memory, disk and network gauges',
      'Short history sparklines',
      'Top processes, with end task in a later version',
    ],
  },
};
const blueprint = computed(() => blueprints[props.tool]);
const meters = ['CPU', 'Memory', 'Disk', 'Network'];
const logRows = [82, 64, 91, 48, 73, 58, 86, 40];
const fileRows = [58, 42, 71, 36, 64, 50];
</script>

<template>
  <section class="tool-preview" :data-tool="props.tool">
    <div class="callout" data-tone="warn">
      <AppIcon name="info" />
      <div>
        <strong>{{ blueprint.title }} is not available yet</strong>
        <p>
          {{ props.device[props.tool].explanation }} {{ blueprint.requirement }} This is a preview
          of the layout; nothing here is connected to {{ props.device.name }}.
        </p>
      </div>
    </div>

    <div class="preview-frame panel" inert aria-hidden="true">
      <template v-if="props.tool === 'files'">
        <div class="preview-bar">
          <span class="crumbs">
            <AppIcon name="folder" /><span>Home</span><AppIcon name="chevronRight" /><span
              >Documents</span
            >
          </span>
          <span class="session-spacer" />
          <button type="button" class="small" disabled><AppIcon name="upload" />Upload</button>
          <button type="button" class="small" disabled><AppIcon name="download" />Download</button>
          <button type="button" class="small" disabled><AppIcon name="plus" />New folder</button>
        </div>
        <table class="preview-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Size</th>
              <th>Modified</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(width, index) in fileRows" :key="index">
              <td>
                <span class="preview-cell">
                  <AppIcon :name="index % 3 === 0 ? 'folder' : 'file'" />
                  <span class="skeleton line" :style="undefined" :data-w="width" />
                </span>
              </td>
              <td><span class="skeleton line" data-w="30" /></td>
              <td><span class="skeleton line" data-w="55" /></td>
            </tr>
          </tbody>
        </table>
        <div class="dropzone"><AppIcon name="upload" />Drop files here to upload</div>
      </template>

      <template v-else-if="props.tool === 'logs'">
        <div class="preview-bar">
          <div class="button-group" role="group">
            <button type="button" class="small" disabled aria-pressed="true">All</button>
            <button type="button" class="small" disabled>Info</button>
            <button type="button" class="small" disabled>Warning</button>
            <button type="button" class="small" disabled>Error</button>
          </div>
          <label class="search-field">
            <AppIcon name="search" />
            <input type="search" placeholder="Search logs" disabled />
          </label>
          <button type="button" class="small" disabled><AppIcon name="download" />Export</button>
        </div>
        <div class="preview-log">
          <div v-for="(width, index) in logRows" :key="index" class="preview-log-row">
            <span class="skeleton line" data-w="18" />
            <span class="skeleton line" :data-w="index % 4 === 2 ? 14 : 10" />
            <span class="skeleton line" :data-w="width" />
          </div>
        </div>
      </template>

      <template v-else>
        <div class="meter-grid">
          <div v-for="meter in meters" :key="meter" class="meter">
            <span class="meter-label">{{ meter }}</span>
            <span class="meter-value">—</span>
            <span class="meter-track"><span class="meter-fill" /></span>
          </div>
        </div>
        <div class="preview-chart">
          <span class="skeleton line" data-w="100" />
          <span class="skeleton line" data-w="100" />
          <span class="skeleton line" data-w="100" />
        </div>
        <table class="preview-table">
          <thead>
            <tr>
              <th>Process</th>
              <th>CPU</th>
              <th>Memory</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(width, index) in fileRows.slice(0, 5)" :key="index">
              <td><span class="skeleton line" :data-w="width" /></td>
              <td><span class="skeleton line" data-w="24" /></td>
              <td><span class="skeleton line" data-w="32" /></td>
            </tr>
          </tbody>
        </table>
      </template>
    </div>

    <div class="panel tool-plan">
      <h2>What {{ blueprint.title.toLowerCase() }} will do</h2>
      <p>{{ blueprint.summary }}</p>
      <ul class="feature-list">
        <li v-for="item in blueprint.bullets" :key="item"><AppIcon name="check" />{{ item }}</li>
      </ul>
    </div>
  </section>
</template>
